import React from "react";
import { flagUrl, flagUrlSquare } from "../../shared/flags";

// Dev gallery: start-page "player card + big flag" layout ideas. The flag spans (roughly)
// the card height and is minimal (no caption). Each layout is shown with a ROUND flag and
// a RECTANGULAR flag. Route: /playerbar
const NAME = "VioletVortex";
const CODE = "pt";
const SWATCH = "#37c8e0";
const CARD_BG = "#141b2b";
const LINE = "rgba(255,255,255,0.10)";
const H = 60; // card height
type Style = "round" | "rect";

const swatch = <span style={{ width: 26, height: 26, borderRadius: 5, background: SWATCH, flex: "0 0 auto" }} />;
const name = <span style={{ fontFamily: "'Rajdhani',sans-serif", fontWeight: 800, fontSize: 18, color: "#eaf0ff", flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{NAME}</span>;
const keyBtn = (t: string) => <span style={{ fontFamily: "'Rajdhani',sans-serif", fontWeight: 800, fontSize: 12, color: "#cfd6e6", border: `1px solid ${LINE}`, borderRadius: 5, padding: "6px 10px", background: "rgba(255,255,255,0.03)" }}>{t}</span>;
const addChip = <span style={{ fontFamily: "'Rajdhani',sans-serif", fontWeight: 700, fontSize: 12.5, color: SWATCH, border: `1px dashed rgba(55,200,224,0.5)`, background: "rgba(55,200,224,0.06)", borderRadius: 8, padding: "7px 11px", whiteSpace: "nowrap" }}>+ Add player</span>;
const keys = <span style={{ display: "inline-flex", gap: 6, flex: "0 0 auto" }}>{keyBtn("LEFT")}{keyBtn("A")}</span>;

// A flag of a given pixel size. round = circle; rect = 4:3 landscape (uses the square set).
function flag(style: Style, h: number, flush?: "left" | "full") {
  if (style === "round") {
    return <img src={flagUrl(CODE)} alt="" width={h} height={h} style={{ display: "block", borderRadius: "50%", flex: "0 0 auto", boxShadow: "0 0 0 1px rgba(255,255,255,0.28)" }} />;
  }
  const w = flush ? Math.round(h * 1.15) : Math.round(h * 1.4);
  return (
    <span style={{ display: "block", flex: "0 0 auto", width: w, height: h, overflow: "hidden", borderRadius: flush === "full" ? 0 : 5, border: flush === "full" ? "none" : `1px solid ${LINE}` }}>
      <img src={flagUrlSquare(CODE)} alt="" width={w} height={h} style={{ display: "block", width: "100%", height: "100%", objectFit: "cover" }} />
    </span>
  );
}

const cardBase: React.CSSProperties = { display: "flex", alignItems: "center", height: H, boxSizing: "border-box", background: CARD_BG, border: `1px solid ${LINE}`, borderRadius: 8, overflow: "hidden" };

function Layout({ n, style }: { n: number; style: Style }) {
  const clickable = { cursor: "pointer" } as React.CSSProperties;
  switch (n) {
    case 1: // Flag fills the left of the card, full height, flush.
      return (
        <div style={cardBase}>
          <span style={{ ...clickable, height: "100%", display: "flex", alignItems: "center" }}>{flag(style, H, "full")}</span>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0, padding: "0 12px" }}>{swatch}{name}{keys}{addChip}</div>
        </div>
      );
    case 2: // Big flag right after the colour swatch, inside the card.
      return (
        <div style={{ ...cardBase, gap: 12, padding: "0 12px" }}>
          {swatch}
          <span style={clickable}>{flag(style, H - 16)}</span>
          {name}{keys}{addChip}
        </div>
      );
    case 3: // Flag as its OWN card to the left of the player card.
      return (
        <div style={{ display: "flex", gap: 10, alignItems: "stretch" }}>
          <div style={{ ...cardBase, ...clickable, width: style === "round" ? H : Math.round(H * 1.4), justifyContent: "center" }}>{flag(style, H - 14)}</div>
          <div style={{ ...cardBase, gap: 12, padding: "0 12px", flex: 1, minWidth: 0 }}>{swatch}{name}{keys}{addChip}</div>
        </div>
      );
    case 4: // Flag as its own card to the RIGHT of the player card.
      return (
        <div style={{ display: "flex", gap: 10, alignItems: "stretch" }}>
          <div style={{ ...cardBase, gap: 12, padding: "0 12px", flex: 1, minWidth: 0 }}>{swatch}{name}{keys}{addChip}</div>
          <div style={{ ...cardBase, ...clickable, width: style === "round" ? H : Math.round(H * 1.4), justifyContent: "center" }}>{flag(style, H - 14)}</div>
        </div>
      );
    default: // 5: Big flag on the RIGHT edge of the card (portrait), flush.
      return (
        <div style={cardBase}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0, padding: "0 12px" }}>{swatch}{name}{keys}{addChip}</div>
          <span style={{ ...clickable, height: "100%", display: "flex", alignItems: "center" }}>{flag(style, H, "full")}</span>
        </div>
      );
  }
}

const LABELS = ["Flag fills left of card (full height)", "Big flag beside the colour", "Flag = its own card (left)", "Flag = its own card (right)", "Flag fills right of card"];

export default function PlayerBarDemo() {
  return (
    <div style={{ maxWidth: 820, margin: "0 auto", padding: "8px 16px 60px", fontFamily: "'Rajdhani', sans-serif", color: "#eaf0ff" }}>
      <h1 style={{ fontFamily: "'Orbitron', sans-serif", letterSpacing: 2, fontSize: 22 }}>Player card + flag layouts</h1>
      <p style={{ color: "#8995b3", fontSize: 14, marginTop: 4 }}>
        Start-page ideas where your flag is big (spans the card height) and minimal. Each layout is shown with a
        <b> round</b> flag and a <b> rectangular</b> flag. The card here is the full-width player row.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 20, marginTop: 16 }}>
        {[1, 2, 3, 4, 5].map((n) => (
          <div key={n} style={{ background: "#0b0f17", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "14px 16px" }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 12 }}>
              <span style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, color: "#37c8e0", fontSize: 15 }}>{n}</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#c3ccdf" }}>{LABELS[n - 1]}</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div><div style={{ fontSize: 11, color: "#5f6a86", marginBottom: 4, letterSpacing: 1 }}>ROUND</div><Layout n={n} style="round" /></div>
              <div><div style={{ fontSize: 11, color: "#5f6a86", marginBottom: 4, letterSpacing: 1 }}>RECTANGULAR</div><Layout n={n} style="rect" /></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
